// ---------------------------------------------------------------------------
// analytics.js
// ---------------------------------------------------------------------------
// Dual-sink analytics wrapper. Every helper here fires:
//   1. Google Analytics 4 via window.gtag  — historical reporting in GA4 UI.
//   2. Our own K9os first-party beacon POST /api/track — powers the real-
//      time dashboards inside /admin/analytics (works even when GA is
//      blocked by ad-blockers, and gives us instant totals without the
//      GA4 24-hour aggregation delay).
//
// Both sinks are best-effort and silently swallow errors so a broken
// tracker can never break the app.
//
// Property: K9os Web · Measurement ID: G-XTF8V56P90
// ---------------------------------------------------------------------------
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Session identity — a stable UUID-ish token stored in sessionStorage so
// every event fired inside a single browser session shares a `session_id`.
// This lets the backend compute unique visitors without cookies.
// ---------------------------------------------------------------------------
const SESSION_KEY = 'k9os_analytics_sid';
const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      // Not cryptographically strong — we just need collision-resistant
      // uniqueness across concurrent visitors. `crypto.randomUUID()`
      // when available, else a Math.random fallback for old browsers.
      sid = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `sid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (_e) { return 'anon'; }
};

// ---------------------------------------------------------------------------
// Backend beacon — POST to /api/track. Uses navigator.sendBeacon when
// available so events survive page-unload (link clicks that navigate
// away don't drop the event). Falls back to fetch(keepalive) elsewhere.
// ---------------------------------------------------------------------------
const backendBase = () => {
  // Match the same BACKEND URL used by src/api.js so /api/track hits the
  // right host in every environment (preview, prod, localhost dev).
  const raw = process.env.REACT_APP_BACKEND_URL || '';
  return raw.replace(/\/$/, '');
};

const sendBeacon = (body) => {
  try {
    const url = `${backendBase()}/api/track`;
    const payload = JSON.stringify({ session_id: getSessionId(), ...body });
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    // Fallback for browsers without sendBeacon (rare).
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch (_e) { /* swallow — never break the app */ }
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Fire a raw GA4 event AND mirror to our first-party beacon. */
export const trackEvent = (name, params = {}) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    }
  } catch (_e) { /* swallow */ }
  // Mirror to first-party — the backend enforces its own allow-list of
  // event types so unknown names are safely dropped server-side.
  sendBeacon({
    event_type: name,
    path: (typeof window !== 'undefined')
      ? window.location.pathname + window.location.search
      : '',
    referrer: (typeof document !== 'undefined') ? document.referrer : '',
    target: params.cta_label || params.target || '',
    location: params.cta_location || params.location || '',
    region: params.region || '',
  });
};

/**
 * Fire a page_view event for a specific URL. Called both from useGA on
 * every route change and manually if you want to log a virtual pageview
 * (e.g. a modal open counting as a pageview). Also mirrors to the
 * first-party beacon so /admin/analytics gets real-time page counts.
 */
export const trackPageView = (path) => {
  const fullPath = path || (
    typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : ''
  );
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: fullPath,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  } catch (_e) { /* swallow */ }
  sendBeacon({
    event_type: 'page_view',
    path: fullPath,
    referrer: (typeof document !== 'undefined') ? document.referrer : '',
  });
};

/**
 * useGA — mount-once inside <BrowserRouter> to auto-fire GA4 page_view on
 * every SPA route change. Because we set `send_page_view: false` in the
 * gtag config, GA4 will ONLY count pageviews sent from this hook. That gives
 * accurate SPA analytics instead of the misleading single pageview a naive
 * gtag install would report.
 */
export const useGA = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
    // We intentionally depend on the full location — pathname + search
    // changes both count as a new page_view.
  }, [location.pathname, location.search]);
};

/* -------------------------------------------------------------------------
 * Named event helpers — call these from any component so GA4 events stay
 * consistent (typos in event names silently split reports into two rows).
 * Every helper below ALSO hits our first-party beacon.
 * ---------------------------------------------------------------------- */

/** A trainer submitted the demo lead form on any regional / state page. */
export const trackDemoLead = ({ source, region } = {}) => {
  trackEvent('generate_lead', {
    method: 'demo_request_form',
    source: source || 'unknown',
    region: region || 'unspecified',
    // Recommended GA4 conversion event alias
    value: 100,
    currency: 'USD',
    target: source || 'demo_form',
    location: region || 'unspecified',
  });
};

/** A visitor opened the "Message Greg" bubble. */
export const trackMessageGregOpen = () => {
  trackEvent('message_greg_open', {
    location: 'floating_bubble',
    target: 'message_greg',
  });
};

/** A visitor clicked a Book-a-Demo / Get Started / Pricing / Compare CTA. */
export const trackCtaClick = (label, location) => {
  trackEvent('cta_click', {
    cta_label: label || 'unknown',
    cta_location: location || 'unknown',
  });
};

/** A visitor tapped a country / state on the interactive globe. */
export const trackRegionMapTap = (regionSlug) => {
  trackEvent('region_map_tap', {
    region: regionSlug || 'unknown',
    target: regionSlug || 'unknown',
    location: 'region_globe',
  });
};
